import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { auth, db } from '../../firebase/config';
import { 
  signInWithEmailAndPassword, 
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { 
  collection, 
  getDocs, 
  getDoc,
  query, 
  where,
  limit,
  doc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';

function isProfileCompletionRequired(userData = {}) {
  if (!userData || typeof userData !== 'object') return true;
  const requiredFields = ['empID', 'designation', 'phone', 'address', 'country'];
  return requiredFields.some((field) => !String(userData[field] || '').trim());
}

// Initial state
const initialState = {
  user: null,
  userType: null, // 'admin' or 'user'
  isAdmin: false, // explicit field for admin status
  adminViewActive: true, // track if admin is in admin view mode
  canAssessRecruitment: false,
  isAuthenticated: false,
  loading: false,
  error: null,
  employees: [],
  employeesLoading: false,
  employeesError: null,
  profileUpdating: false // separate loading state for profile updates
};

// Async thunk to sign in and check user type
export const signInUser = createAsyncThunk(
  'auth/signInUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // Sign in with email and password
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if the user exists in myeliners collection using email as document ID
      const userDocRef = doc(db, 'myeliners', user.email);
      const userDoc = await getDoc(userDocRef);
      
      // User is not in myeliners collection
      if (!userDoc.exists()) {
        // Sign out the user since they are not authorized
        await signOut(auth);
        return rejectWithValue('User not authorized. Not found in employees list.');
      }
      
      // Get the user data
      const userData = userDoc.data();
      
      // Check user type
      const userType = userData.userType || 'user'; // Default to 'user' if not specified
      
      return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || userData.name || email.split('@')[0],
        photoURL: user.photoURL,
        userType,
        empID: userData.empID,
        requiresProfileCompletion: isProfileCompletionRequired(userData),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch all employees from myeliners collection
export const fetchEmployees = createAsyncThunk(
  'auth/fetchEmployees',
  async (_, { rejectWithValue }) => {
    try {
      const myelinersRef = collection(db, 'myeliners');
      const querySnapshot = await getDocs(myelinersRef);
      
      const employees = [];
      querySnapshot.forEach((doc) => {
        employees.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return employees;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkRecruitmentAssessorAccess = createAsyncThunk(
  'auth/checkRecruitmentAssessorAccess',
  async (email, { rejectWithValue }) => {
    try {
      if (!email) return false;
      const recruitmentRef = collection(db, 'otherDBs', 'recruitments', 'recruitments');
      const [assessorOneSnap, assessorTwoSnap] = await Promise.all([
        getDocs(query(recruitmentRef, where('assessorOne', '==', email), limit(1))),
        getDocs(query(recruitmentRef, where('assessorTwo', '==', email), limit(1))),
      ]);
      return !assessorOneSnap.empty || !assessorTwoSnap.empty;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to evaluate recruitment assessor access');
    }
  }
);

// Async thunk to update user profile
export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async ({ email, profileData }, { rejectWithValue }) => {
    try {
      // Get user document using email as document ID
      const userDocRef = doc(db, 'myeliners', email);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      // Add updatedAt timestamp to the profileData
      const dataToUpdate = {
        ...profileData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(userDocRef, dataToUpdate);

      // Return updated profile data to update the state
      return {
        id: email,
        email,
        ...profileData
      };
    } catch (error) {
      console.error('Error updating profile:', error);
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

// Async thunk to sign up user
export const signUpUser = createAsyncThunk(
  'auth/signUpUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // First check if the user exists in myeliners collection
      const myelinersRef = collection(db, 'myeliners');
      const q = query(myelinersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      // User is not in myeliners collection
      if (querySnapshot.empty) {
        return rejectWithValue('Email not found in employee database. Please contact your administrator.');
      }
      
      // Get the user data from myeliners collection
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      
      // Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check user type
      const userType = userData.userType || 'user'; // Default to 'user' if not specified
      
      return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || userData.name || email.split('@')[0],
        photoURL: user.photoURL,
        userType,
        empID: userData.empID,
        requiresProfileCompletion: isProfileCompletionRequired(userData),
      };
    } catch (error) {
      // Handle specific Firebase Auth errors
      if (error.code === 'auth/email-already-in-use') {
        return rejectWithValue('An account with this email already exists. Please try logging in instead.');
      } else if (error.code === 'auth/weak-password') {
        return rejectWithValue('Password should be at least 6 characters long.');
      } else if (error.code === 'auth/invalid-email') {
        return rejectWithValue('Please enter a valid email address.');
      }
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to reset password
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      // First check if the user exists in myeliners collection
      const myelinersRef = collection(db, 'myeliners');
      const q = query(myelinersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      // User is not in myeliners collection
      if (querySnapshot.empty) {
        return rejectWithValue('Email not found in employee database. Please contact your administrator.');
      }
      
      // Send password reset email
      await sendPasswordResetEmail(auth, email);
      
      return { email, message: 'Password reset email sent successfully' };
    } catch (error) {
      // Handle specific Firebase Auth errors
      if (error.code === 'auth/user-not-found') {
        return rejectWithValue('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        return rejectWithValue('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        return rejectWithValue('Too many reset attempts. Please try again later.');
      }
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to sign out
export const signOutUser = createAsyncThunk(
  'auth/signOutUser',
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      console.log('setUser action called with payload:', action.payload);
      
      if (action.payload) {
        state.user = action.payload;
        state.userType = action.payload.userType || 'user';
        state.isAdmin = state.userType === 'admin';
        state.adminViewActive = state.isAdmin; // set initial admin view to true for admins
        state.canAssessRecruitment = Boolean(action.payload.canAssessRecruitment);
        state.isAuthenticated = true;
        
        console.log('Auth state after setUser update:', {
          isAuthenticated: state.isAuthenticated,
          userType: state.userType,
          isAdmin: state.isAdmin,
          adminViewActive: state.adminViewActive,
          user: state.user ? { ...state.user } : null
        });
      } else {
        state.user = null;
        state.userType = null;
        state.isAdmin = false;
        state.adminViewActive = false;
        state.canAssessRecruitment = false;
        state.isAuthenticated = false;
        
        console.log('Auth state reset to null');
      }
    },
    toggleAdminView: (state) => {
      // Only admins can toggle this setting
      if (state.isAdmin) {
        state.adminViewActive = !state.adminViewActive;
        console.log('Admin view toggled:', state.adminViewActive);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle sign in
      .addCase(signInUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.userType = action.payload.userType;
        state.isAdmin = action.payload.userType === 'admin';
        state.adminViewActive = state.isAdmin; // set initial admin view to true for admins
        state.canAssessRecruitment = Boolean(action.payload.canAssessRecruitment);
        state.error = null;
      })
      .addCase(signInUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.userType = null;
        state.isAdmin = false;
        state.adminViewActive = false;
        state.canAssessRecruitment = false;
        state.error = action.payload;
      })
      // Handle sign up
      .addCase(signUpUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.userType = action.payload.userType;
        state.isAdmin = action.payload.userType === 'admin';
        state.adminViewActive = state.isAdmin;
        state.canAssessRecruitment = Boolean(action.payload.canAssessRecruitment);
        state.error = null;
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.userType = null;
        state.isAdmin = false;
        state.adminViewActive = false;
        state.canAssessRecruitment = false;
        state.error = action.payload;
      })
      // Handle password reset
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle sign out
      .addCase(signOutUser.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.userType = null;
        state.isAdmin = false;
        state.adminViewActive = false;
        state.canAssessRecruitment = false;
        state.error = null;
      })
      .addCase(checkRecruitmentAssessorAccess.fulfilled, (state, action) => {
        state.canAssessRecruitment = Boolean(action.payload);
      })
      .addCase(checkRecruitmentAssessorAccess.rejected, (state) => {
        state.canAssessRecruitment = false;
      })
      // Handle fetch employees
      .addCase(fetchEmployees.pending, (state) => {
        state.employeesLoading = true;
        state.employeesError = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.employeesLoading = false;
        state.employees = action.payload;
        state.employeesError = null;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.employeesLoading = false;
        state.employeesError = action.payload;
      })
      // Handle update user profile
      .addCase(updateUserProfile.pending, (state) => {
        state.profileUpdating = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.profileUpdating = false;
        
        // Update the employee in the employees array
        if (state.employees.length > 0) {
          state.employees = state.employees.map(emp => 
            emp.email === action.payload.email 
              ? { ...emp, ...action.payload }
              : emp
          );
        }
        
        // If this is the current user, update the user field as well
        if (state.user && state.user.email === action.payload.email) {
          state.user = {
            ...state.user,
            ...action.payload
          };
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.profileUpdating = false;
        state.error = action.payload;
      });
  },
});

export const { setUser, toggleAdminView } = authSlice.actions;

export default authSlice.reducer; 